import { NavLink } from "react-router";
import {
  FileText,
  MessageCircle,
  Gift,
  FolderOpen,
  Compass,
  ArrowRight,
  Clock,
  AlertCircle,
  CheckCircle2,
  Calendar,
  TrendingUp,
  BookOpen,
  Bell,
  Sparkles,
  MapPin,
  Star,
  BrainCircuit,
  User,
  GraduationCap,
} from "lucide-react";
import { motion } from "motion/react";
import { useAuth } from "../../context/AuthContext";
import { computeProfileCompletion } from "../../utils/profileCompletion";
import {
  MOCK_APPLICATIONS,
  MOCK_DEADLINES,
  MOCK_DOCUMENTS,
} from "../../data/mockData";
import { RiasecCode } from "../../Data/mockDataCareerTest";

const QUICK_ACTIONS = [
  // {
  //   icon: <FileText className="w-5 h-5" />,
  //   label: "View Applications",
  //   sub: "4 active",
  //   to: "/dashboard/applications",
  //   color: "text-violet-600 bg-violet-50",
  //   border: "border-violet-100",
  // },
  // {
  //   icon: <MessageCircle className="w-5 h-5" />,
  //   label: "Chat Counselor",
  //   sub: "1 unread",
  //   to: "/dashboard/chat",
  //   color: "text-cyan-600 bg-cyan-50",
  //   border: "border-cyan-100",
  //   badge: true,
  // },
  // {
  //   icon: <Gift className="w-5 h-5" />,
  //   label: "Rewards",
  //   sub: "2,450 pts",
  //   to: "/dashboard/rewards",
  //   color: "text-orange-600 bg-orange-50",
  //   border: "border-orange-100",
  // },
  {
    icon: <User className="w-5 h-5" />,
    label: "Profile",
    to: "/dashboard/profile",
    color: "text-orange-600 bg-orange-50",
    border: "border-orange-100",
  },
  // {
  //   icon: <GraduationCap className="w-5 h-5" />,
  //   label: "Exam Hub",
  //   to: "/dashboard/exam-hub",
  //   color: "text-cyan-600 bg-cyan-50",
  //   border: "border-cyan-100",
  // },
  {
    icon: <Sparkles className="w-5 h-5" />,
    label: "Course Finder",
    to: "/dashboard/course-finder",
    color: "text-violet-600 bg-violet-50",
    border: "border-violet-100",
  },
  {
    icon: <BrainCircuit className="w-5 h-5" />,
    label: "Career Test",
    to: "/dashboard/career-test-result",
    color: "text-emerald-600 bg-emerald-50",
    border: "border-emerald-100",
  },
  {
    icon: <Compass className="w-5 h-5" />,
    label: "Explore Unis",
    to: "/dashboard/explore",
    color: "text-lime-600 bg-lime-50",
    border: "border-lime-100",
  },
];

const statusConfig: Record<
  string,
  { label: string; color: string; bg: string; icon: React.ReactNode }
> = {
  offer_received: {
    label: "Offer Received",
    color: "text-green-700",
    bg: "bg-green-50 border-green-200",
    icon: <CheckCircle2 className="w-3.5 h-3.5" />,
  },
  under_review: {
    label: "Under Review",
    color: "text-amber-700",
    bg: "bg-amber-50 border-amber-200",
    icon: <Clock className="w-3.5 h-3.5" />,
  },
  submitted: {
    label: "Submitted",
    color: "text-blue-700",
    bg: "bg-blue-50 border-blue-200",
    icon: <CheckCircle2 className="w-3.5 h-3.5" />,
  },
  draft: {
    label: "Draft",
    color: "text-slate-600",
    bg: "bg-slate-50 border-slate-200",
    icon: <FileText className="w-3.5 h-3.5" />,
  },
};

const ACTIVITY = [
  {
    icon: "🎉",
    text: "Taylor's University sent you an offer letter!",
    time: "2 hours ago",
    type: "success",
  },
  {
    icon: "📋",
    text: "Your UCSI application moved to Under Review",
    time: "1 day ago",
    type: "info",
  },
  {
    icon: "💬",
    text: "Kak Sarah sent you a message",
    time: "2 days ago",
    type: "message",
  },
  {
    icon: "📄",
    text: "SPM Certificate verified successfully",
    time: "3 days ago",
    type: "doc",
  },
  {
    icon: "🎯",
    text: "2 new scholarships matched your profile",
    time: "5 days ago",
    type: "reward",
  },
];

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

export default function DashboardHome() {
  const { user } = useAuth();
  if (!user) return null;

  const profileCompletion = computeProfileCompletion(user);
  const verifiedDocs = MOCK_DOCUMENTS.filter(
    (d) => d.status === "verified",
  ).length;
  const totalRequiredDocs = MOCK_DOCUMENTS.filter((d) => d.required).length;
  const overallProgress = Math.round(
    (MOCK_APPLICATIONS.filter((a) => a.status !== "draft").length /
      MOCK_APPLICATIONS.length) *
      100,
  );
  const hasCourseFinderData =
    !!user.courseFinderResult || !!user.estimatedResult;
  const hasCareerTestResult = !!user.careerTestResult;

  const COUNSELOR_HELPS = [
    "Compare course and university options",
    "Check entry requirements",
    "Explain fees, scholarships and rebates",
    "Help with application steps",
    "Guide parents if needed",
  ];

  const RECOMMENDED_UNIS = [
    {
      name: "Taylor's University",
      short: "TU",
      location: "Subang Jaya, Selangor",
      type: "local",
      why: ["Matches your interests", "May have scholarship options"],
      color: "#F97316",
    },
    {
      name: "UCSI University",
      short: "UC",
      location: "Kuala Lumpur",
      type: "local",
      why: ["Offers your study interest", "Suitable for local study"],
      color: "#EC4899",
    },
    {
      name: "APU",
      short: "AP",
      location: "Kuala Lumpur",
      type: "local",
      why: ["Top for technology courses", "Counsellor check recommended"],
      color: "#7C3AED",
    },
  ];

  return (
    <div className="p-4 lg:p-8 max-w-5xl mx-auto">
      {/* Greeting */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mb-8"
      >
        <p
          className="text-[14px] text-slate-500 mb-1"
          style={{ fontWeight: 500 }}
        >
          {getGreeting()},
        </p>
        <h1
          className="text-[28px] lg:text-[36px] text-slate-900 tracking-tight"
          style={{ fontWeight: 900 }}
        >
          {user.name} 👋
        </h1>
        <p
          className="text-[14px] text-slate-500 mt-1"
          style={{ fontWeight: 400 }}
        >
          Here's what's happening with your university journey.
        </p>
      </motion.div>

      {!hasCareerTestResult && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.04 }}
          className="mb-4 bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 rounded-3xl p-5 flex flex-col sm:flex-row items-center gap-4 text-white shadow-lg shadow-emerald-500/20"
        >
          <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <BrainCircuit className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <p
              className="text-[15px] text-white mb-0.5"
              style={{ fontWeight: 700 }}
            >
              Discover Your Career Personality
            </p>
            <p
              className="text-[12px] text-emerald-100"
              style={{ fontWeight: 400 }}
            >
              Take the 5-minute RIASEC test to find out which fields and careers
              suit you best.
            </p>
          </div>
          <NavLink
            to="/dashboard/career-test"
            className="flex-shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white text-emerald-700 text-[13px] hover:bg-emerald-50 transition-colors shadow-sm"
            style={{ fontWeight: 700 }}
          >
            Start Test <ArrowRight className="w-4 h-4" />
          </NavLink>
        </motion.div>
      )}

      {/* Course Finder CTA (if not done) */}
      {!hasCourseFinderData && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.05 }}
          className="mb-6 bg-gradient-to-r from-violet-600 via-purple-600 to-indigo-600 rounded-3xl p-5 flex flex-col sm:flex-row items-center gap-4 text-white shadow-lg shadow-violet-500/20"
        >
          <div className="w-12 h-12 rounded-2xl bg-white/20 flex items-center justify-center flex-shrink-0">
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 text-center sm:text-left">
            <p
              className="text-[15px] text-white mb-0.5"
              style={{ fontWeight: 700 }}
            >
              Find Your Ideal Course
            </p>
            <p
              className="text-[12px] text-violet-200"
              style={{ fontWeight: 400 }}
            >
              Take the 5-minute Course Finder quiz to get personalised study
              direction and university suggestions.
            </p>
          </div>
          <NavLink
            to="/course-finder"
            className="flex-shrink-0 flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white text-violet-700 text-[13px] hover:bg-violet-50 transition-colors shadow-sm"
            style={{ fontWeight: 700 }}
          >
            Start Course Finder <ArrowRight className="w-4 h-4" />
          </NavLink>
        </motion.div>
      )}

      {/* Stats row */}
      {/* <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
      >
        {[
          {
            label: "Applications",
            value: "4",
            sub: "3 active",
            color: "from-violet-600 to-purple-600",
            icon: <FileText className="w-4 h-4" />,
          },
          {
            label: "Offer Received",
            value: "1",
            sub: "Taylor's University",
            color: "from-green-500 to-emerald-500",
            icon: <CheckCircle2 className="w-4 h-4" />,
          },
          {
            label: "Scholarships",
            value: "5",
            sub: "matches found",
            color: "from-orange-500 to-amber-500",
            icon: <Gift className="w-4 h-4" />,
          },
          {
            label: "EduPoints",
            value: user.points.toLocaleString(),
            sub: user.tier + " tier",
            color: "from-cyan-500 to-blue-500",
            icon: <TrendingUp className="w-4 h-4" />,
          },
        ].map((stat, i) => (
          <div
            key={i}
            className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex flex-col gap-3"
          >
            <div
              className={`w-9 h-9 rounded-xl bg-gradient-to-br ${stat.color} flex items-center justify-center text-white shadow-md`}
            >
              {stat.icon}
            </div>
            <div>
              <p
                className="text-[26px] text-slate-900 leading-none"
                style={{ fontWeight: 900 }}
              >
                {stat.value}
              </p>
              <p
                className="text-[11px] text-slate-500 mt-1"
                style={{ fontWeight: 500 }}
              >
                {stat.label}
              </p>
              <p
                className="text-[11px] text-slate-400"
                style={{ fontWeight: 400 }}
              >
                {stat.sub}
              </p>
            </div>
          </div>
        ))}
      </motion.div> */}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left column */}
        <div className="lg:col-span-2 flex flex-col gap-6">
          {/* Profile completion */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6"
          >
            <div className="flex items-center justify-between mb-4">
              <h3
                className="text-[16px] text-slate-900"
                style={{ fontWeight: 700 }}
              >
                Profile Completion
              </h3>
              <NavLink
                to="/dashboard/profile"
                className="text-[12px] text-violet-600 flex items-center gap-1 hover:text-violet-700"
                style={{ fontWeight: 600 }}
              >
                Complete now <ArrowRight className="w-3 h-3" />
              </NavLink>
            </div>
            <div className="flex items-center gap-4 mb-4">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center text-white text-[18px]"
                style={{ backgroundColor: user.avatarColor, fontWeight: 900 }}
              >
                {user.name.charAt(0)}
              </div>
              <div className="flex-1">
                <p
                  className="text-[15px] text-slate-900 mb-0.5"
                  style={{ fontWeight: 600 }}
                >
                  {user.name}
                </p>
                <p
                  className="text-[12px] text-slate-500"
                  style={{ fontWeight: 400 }}
                >
                  {user.email}
                </p>
              </div>
              <div className="text-right">
                <p
                  className="text-[24px] text-violet-600"
                  style={{ fontWeight: 900 }}
                >
                  {profileCompletion}%
                </p>
                <p
                  className="text-[11px] text-slate-400"
                  style={{ fontWeight: 500 }}
                >
                  complete
                </p>
              </div>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2 mb-3">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${profileCompletion}%` }}
                transition={{ duration: 1, delay: 0.3 }}
                className="h-2 rounded-full bg-gradient-to-r from-violet-600 to-purple-500"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              {/* {[
                { label: "Add passport photo", done: false },
                { label: "Upload recommendation letter", done: false },
                { label: "Complete bank statement", done: false },
              ].map((task, i) => (
                <div
                  key={i}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-100"
                >
                  <AlertCircle className="w-3 h-3 text-amber-500" />
                  <span
                    className="text-[11px] text-amber-700"
                    style={{ fontWeight: 500 }}
                  >
                    {task.label}
                  </span>
                </div>
              ))} */}
              {profileCompletion < 100 && (
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-100">
                  <AlertCircle className="w-3 h-3 text-amber-500" />
                  <span
                    className="text-[11px] text-amber-700"
                    style={{ fontWeight: 500 }}
                  >
                    Complete your profile
                  </span>
                </div>
              )}
            </div>
          </motion.div>

          {/* Applications snapshot */}
          {/* <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6"
          >
            <div className="flex items-center justify-between mb-5">
              <h3
                className="text-[16px] text-slate-900"
                style={{ fontWeight: 700 }}
              >
                Active Applications
              </h3>
              <NavLink
                to="/dashboard/applications"
                className="text-[12px] text-violet-600 flex items-center gap-1 hover:text-violet-700"
                style={{ fontWeight: 600 }}
              >
                View all <ArrowRight className="w-3 h-3" />
              </NavLink>
            </div>
            <div className="flex flex-col gap-3">
              {MOCK_APPLICATIONS.slice(0, 3).map((app) => {
                const cfg = statusConfig[app.status];
                return (
                  <div
                    key={app.id}
                    className="flex items-center gap-4 p-3 rounded-2xl bg-slate-50/50 hover:bg-slate-50 transition-colors"
                  >
                    <div className="w-10 h-10 rounded-xl bg-white border border-slate-100 flex items-center justify-center text-[18px] shadow-sm flex-shrink-0">
                      {app.flag}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p
                        className="text-[13px] text-slate-900 truncate"
                        style={{ fontWeight: 600 }}
                      >
                        {app.university}
                      </p>
                      <p
                        className="text-[11px] text-slate-400 truncate"
                        style={{ fontWeight: 400 }}
                      >
                        {app.course}
                      </p>
                    </div>
                    <div
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] ${cfg.color} ${cfg.bg} flex-shrink-0`}
                      style={{ fontWeight: 600 }}
                    >
                      {cfg.icon}
                      {cfg.label}
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div> */}

          {/* Recommended Universities */}
          {/* <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.23 }}
            className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6"
          >
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 text-orange-500" />
                <h3
                  className="text-[16px] text-slate-900"
                  style={{ fontWeight: 700 }}
                >
                  Recommended Universities
                </h3>
              </div>
              <NavLink
                to="/dashboard/explore"
                className="text-[12px] text-violet-600 flex items-center gap-1 hover:text-violet-700"
                style={{ fontWeight: 600 }}
              >
                View all <ArrowRight className="w-3 h-3" />
              </NavLink>
            </div>
            <p
              className="text-[12px] text-slate-400 mb-4"
              style={{ fontWeight: 400 }}
            >
              Universities SCS Education can help you compare based on your
              profile.
            </p>
            <div className="flex flex-col gap-3">
              {RECOMMENDED_UNIS.map((uni, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3 p-3.5 rounded-2xl bg-slate-50/60 hover:bg-slate-50 transition-colors"
                >
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-white text-[11px] flex-shrink-0 shadow-sm"
                    style={{
                      backgroundColor: uni.color,
                      fontFamily: "Inter, sans-serif",
                      fontWeight: 800,
                    }}
                  >
                    {uni.short}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className="text-[13px] text-slate-900"
                      style={{ fontWeight: 600 }}
                    >
                      {uni.name}
                    </p>
                    <div className="flex items-center gap-1 mt-0.5">
                      <MapPin className="w-3 h-3 text-slate-400" />
                      <span
                        className="text-[11px] text-slate-400"
                        style={{ fontWeight: 400 }}
                      >
                        {uni.location}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {uni.why.map((w, wi) => (
                        <span
                          key={wi}
                          className="text-[10px] px-2 py-0.5 rounded-full bg-violet-50 text-violet-600 border border-violet-100"
                          style={{ fontWeight: 600 }}
                        >
                          ✓ {w}
                        </span>
                      ))}
                    </div>
                  </div>
                  <a
                    href="https://wa.me/601112870256"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-shrink-0 text-[11px] text-violet-600 hover:text-violet-700 flex items-center gap-0.5 mt-1"
                    style={{ fontWeight: 600 }}
                  >
                    Check <ArrowRight className="w-3 h-3" />
                  </a>
                </div>
              ))}
            </div>
          </motion.div> */}

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.25 }}
          >
            <h3
              className="text-[16px] text-slate-900 mb-4"
              style={{ fontWeight: 700 }}
            >
              Quick Actions
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {QUICK_ACTIONS.map((action, i) => (
                <NavLink
                  key={i}
                  to={action.to}
                  className={`bg-white rounded-2xl border ${action.border} shadow-sm p-4 flex flex-col gap-3 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 relative`}
                >
                  {action.badge && (
                    <span className="absolute top-3 right-3 w-2 h-2 rounded-full bg-red-500" />
                  )}
                  <div
                    className={`w-10 h-10 rounded-xl ${action.color} flex items-center justify-center`}
                  >
                    {action.icon}
                  </div>
                  <div>
                    <p
                      className="text-[13px] text-slate-900"
                      style={{ fontWeight: 600 }}
                    >
                      {action.label}
                    </p>
                    <p
                      className="text-[11px] text-slate-500"
                      style={{ fontWeight: 400 }}
                    >
                      {action.sub}
                    </p>
                  </div>
                </NavLink>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Right column */}
        <div className="flex flex-col gap-6">
          {/* Upcoming deadlines */}
          {/* <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6"
          >
            <div className="flex items-center gap-2 mb-4">
              <Calendar className="w-4 h-4 text-orange-500" />
              <h3
                className="text-[15px] text-slate-900"
                style={{ fontWeight: 700 }}
              >
                Upcoming Deadlines
              </h3>
            </div>
            <div className="flex flex-col gap-3">
              {MOCK_DEADLINES.map((dl) => (
                <div
                  key={dl.id}
                  className={`p-3 rounded-2xl border ${dl.urgent ? "bg-red-50 border-red-100" : "bg-slate-50 border-slate-100"}`}
                >
                  <p
                    className="text-[12px] text-slate-900 mb-1"
                    style={{ fontWeight: 600 }}
                  >
                    {dl.label}
                  </p>
                  <div className="flex items-center justify-between">
                    <p
                      className="text-[11px] text-slate-500"
                      style={{ fontWeight: 400 }}
                    >
                      {dl.date}
                    </p>
                    <span
                      className={`text-[11px] px-2 py-0.5 rounded-full ${dl.urgent ? "bg-red-100 text-red-600" : "bg-slate-200 text-slate-600"}`}
                      style={{ fontWeight: 700 }}
                    >
                      {dl.daysLeft}d left
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div> */}

          {/* Recent Activity */}
          {/* <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.35 }}
            className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6"
          >
            <div className="flex items-center gap-2 mb-4">
              <Bell className="w-4 h-4 text-violet-500" />
              <h3
                className="text-[15px] text-slate-900"
                style={{ fontWeight: 700 }}
              >
                Recent Activity
              </h3>
            </div>
            <div className="flex flex-col gap-3">
              {ACTIVITY.map((act, i) => (
                <div key={i} className="flex gap-3 items-start">
                  <span className="text-[18px] flex-shrink-0">{act.icon}</span>
                  <div>
                    <p
                      className="text-[12px] text-slate-700 leading-snug"
                      style={{ fontWeight: 500 }}
                    >
                      {act.text}
                    </p>
                    <p
                      className="text-[11px] text-slate-400 mt-0.5"
                      style={{ fontWeight: 400 }}
                    >
                      {act.time}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </motion.div> */}

          {/* Counselor card — enhanced */}
          {/* <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-gradient-to-br from-violet-600 to-purple-700 rounded-3xl p-5 text-white"
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center text-[16px]">
                👩‍💼
              </div>
              <div>
                <p className="text-[13px]" style={{ fontWeight: 700 }}>
                  Kak Sarah
                </p>
                <p
                  className="text-[11px] text-violet-200"
                  style={{ fontWeight: 500 }}
                >
                  Your Dedicated Counsellor
                </p>
                <div className="flex items-center gap-1 mt-0.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                  <p
                    className="text-[10px] text-green-300"
                    style={{ fontWeight: 500 }}
                  >
                    Online now
                  </p>
                </div>
              </div>
            </div>
            <div className="bg-white/10 rounded-2xl p-3 mb-3">
              <p
                className="text-[10px] text-violet-300 mb-2"
                style={{ fontWeight: 600 }}
              >
                YOUR COUNSELLOR CAN HELP WITH:
              </p>
              <div className="flex flex-col gap-1.5">
                {COUNSELOR_HELPS.slice(0, 3).map((h, i) => (
                  <div key={i} className="flex items-center gap-1.5">
                    <CheckCircle2 className="w-3 h-3 text-violet-300 flex-shrink-0" />
                    <span
                      className="text-[11px] text-violet-200"
                      style={{ fontWeight: 400 }}
                    >
                      {h}
                    </span>
                  </div>
                ))}
              </div>
            </div>
            <p
              className="text-[12px] text-violet-200 mb-4 leading-relaxed"
              style={{ fontWeight: 400 }}
            >
              1 unread message waiting for you.
            </p>
            <NavLink
              to="/dashboard/chat"
              className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl bg-white/20 hover:bg-white/30 transition-colors text-[13px] text-white"
              style={{ fontWeight: 600 }}
            >
              <MessageCircle className="w-4 h-4" /> Open Chat
            </NavLink>
            <a
              href="https://wa.me/601112870256"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 w-full py-2 rounded-xl text-[12px] text-violet-300 hover:text-white transition-colors mt-2"
              style={{ fontWeight: 500 }}
            >
              💬 WhatsApp instead
            </a>
          </motion.div> */}
        </div>
      </div>
    </div>
  );
}
